// src/components/TradingBot.js - COMPLETE UPDATED WITH ALWAYS VISIBLE SUMMARY
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  FaPlay, FaStop, FaSave, FaEye, FaEyeSlash,
  FaArrowUp, FaArrowDown, FaSpinner, FaTelegramPlane, FaCog, FaTimes, FaCheckCircle, FaClock, FaInfoCircle
} from 'react-icons/fa';
import { 
  startTradingBot, stopTradingBot, placeOrder,
  loadPositions, loadTrades, loadTradingBotConfig,
  setTradingBotRunning
} from '../redux/slices/tradingSlice';
import PositionTracker from './PositionTracker';
import TradeHistory from './TradeHistory';
import LogViewer from './LogViewer';
import NewsCalendar from './NewsCalendar';
import TelegramConfigModal from './TelegramConfigModal';
import toast from 'react-hot-toast';
import axios from 'axios';

const API_BASE_URL = 'https://gritwears.store/api';

// Starting Modal Component
const StartingModal = ({ isOpen, isStarting, isComplete, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="starting-modal-overlay">
      <div className="starting-modal">
        {isComplete && (
          <button className="starting-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        )}
        
        <div className="starting-modal-content">
          {isStarting && !isComplete ? (
            <>
              <div className="starting-spinner"></div>
              <h2>🚀 Starting Trading Bot</h2>
              <p>Connecting to MetaApi and initializing trading bot...</p>
              <p className="starting-sub-text">This may take a few moments</p>
            </>
          ) : isComplete ? (
            <>
              <div className="starting-success-icon">
                <FaCheckCircle />
              </div>
              <h2>✅ Bot Started Successfully!</h2>
              <p>The trading bot is now running and monitoring your account.</p>
              <p className="starting-sub-text">You can close this window now</p>
              <button className="starting-close-btn" onClick={onClose}>
                Close
              </button>
            </>
          ) : (
            <>
              <div className="starting-spinner"></div>
              <h2>⏳ Please Wait...</h2>
              <p>Bot is starting up...</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Bot Duration Display Component - ALWAYS VISIBLE
const BotDurationDisplay = ({ sessionInfo, isRunning, onClose }) => {
  const [currentRuntime, setCurrentRuntime] = useState('');
  const timerRef = useRef(null);

  // Format duration function
  const formatDuration = (seconds) => {
    if (!seconds || seconds < 0) return 'N/A';
    if (seconds < 60) {
      return `${seconds} second${seconds !== 1 ? 's' : ''}`;
    }
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}${remainingSeconds > 0 ? ` ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}` : ''}`;
    }
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours < 24) {
      let result = `${hours} hour${hours !== 1 ? 's' : ''}`;
      if (remainingMinutes > 0) {
        result += ` ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`;
      }
      if (remainingSeconds > 0 && remainingMinutes === 0) {
        result += ` ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}`;
      }
      return result;
    }
    
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    
    if (days < 7) {
      let result = `${days} day${days !== 1 ? 's' : ''}`;
      if (remainingHours > 0) {
        result += ` ${remainingHours} hour${remainingHours !== 1 ? 's' : ''}`;
      }
      if (remainingMinutes > 0 && remainingHours === 0) {
        result += ` ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`;
      }
      return result;
    }
    
    const weeks = Math.floor(days / 7);
    const remainingDays = days % 7;
    
    if (weeks < 4) {
      let result = `${weeks} week${weeks !== 1 ? 's' : ''}`;
      if (remainingDays > 0) {
        result += ` ${remainingDays} day${remainingDays !== 1 ? 's' : ''}`;
      }
      if (remainingHours > 0 && remainingDays === 0) {
        result += ` ${remainingHours} hour${remainingHours !== 1 ? 's' : ''}`;
      }
      return result;
    }
    
    const months = Math.floor(days / 30);
    const remainingDaysAfterMonths = days % 30;
    
    if (months < 12) {
      let result = `${months} month${months !== 1 ? 's' : ''}`;
      if (remainingDaysAfterMonths > 0) {
        result += ` ${remainingDaysAfterMonths} day${remainingDaysAfterMonths !== 1 ? 's' : ''}`;
      }
      if (remainingHours > 0 && remainingDaysAfterMonths === 0) {
        result += ` ${remainingHours} hour${remainingHours !== 1 ? 's' : ''}`;
      }
      return result;
    }
    
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    let result = `${years} year${years !== 1 ? 's' : ''}`;
    if (remainingMonths > 0) {
      result += ` ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
    }
    if (remainingDaysAfterMonths > 0 && remainingMonths === 0) {
      result += ` ${remainingDaysAfterMonths} day${remainingDaysAfterMonths !== 1 ? 's' : ''}`;
    }
    return result;
  };

  // Calculate runtime
  const calculateRuntime = useCallback(() => {
    if (!sessionInfo) return 'N/A';
    
    // If bot is running and we have start time, calculate live runtime
    if (isRunning && sessionInfo.startTime) {
      const start = new Date(sessionInfo.startTime).getTime();
      const now = Date.now();
      const diffSeconds = Math.floor((now - start) / 1000);
      return formatDuration(diffSeconds);
    }
    
    // If bot is stopped and we have formatted duration, use it
    if (sessionInfo.formattedDuration && sessionInfo.formattedDuration !== 'N/A') {
      return sessionInfo.formattedDuration;
    }
    
    // If bot is stopped and we have duration seconds, calculate it
    if (sessionInfo.durationSeconds) {
      return formatDuration(sessionInfo.durationSeconds);
    }
    
    // If bot is stopped and we have endTime and startTime, calculate manually
    if (!isRunning && sessionInfo.startTime && sessionInfo.endTime) {
      const start = new Date(sessionInfo.startTime).getTime();
      const end = new Date(sessionInfo.endTime).getTime();
      const diffSeconds = Math.floor((end - start) / 1000);
      if (diffSeconds > 0) {
        return formatDuration(diffSeconds);
      }
    }
    
    return 'N/A';
  }, [sessionInfo, isRunning]);

  // Get stop status - FIXED
  const getStopStatus = () => {
    if (isRunning) return '🟢 Still Running';
    if (!sessionInfo) return '⏹️ Not Started';
    
    const reason = sessionInfo.reason;
    if (reason === 'MANUAL') return '✅ Stopped by User';
    if (reason === 'SERVER_SHUTDOWN') return '⚠️ Server Shutdown (Cold Stop)';
    if (reason === 'SERVER_CRASH') return '❌ Server Crash';
    if (reason === 'AUTO_RECOVERY') return '🔄 Auto-Recovery Restart';
    if (reason === 'CONNECTION_LOST') return '🔌 Connection Lost';
    if (reason === 'ERROR') return '❌ Stopped due to Error';
    if (sessionInfo.endTime) return '⏹️ Stopped';
    return '⏹️ Not Started';
  };

  // Get shutdown reason display - FIXED
  const getReasonDisplay = (reason) => {
    if (isRunning) return '🔄 Bot is running';
    if (!reason) return '⏹️ Not Started';
    const reasons = {
      'MANUAL': '🛑 Stopped by User',
      'SERVER_SHUTDOWN': '⚠️ Server Shutdown (Cold Stop)',
      'SERVER_CRASH': '❌ Server Crash',
      'AUTO_RECOVERY': '🔄 Auto-Recovery Restart',
      'CONNECTION_LOST': '🔌 Connection Lost',
      'ERROR': '❌ Stopped due to Error'
    };
    return reasons[reason] || reason;
  };

  // Get color for shutdown reason
  const getReasonColor = (reason) => {
    if (isRunning) return '#4CAF50';
    if (!reason) return '#666';
    if (reason === 'MANUAL') return '#4CAF50';
    if (reason === 'SERVER_SHUTDOWN' || reason === 'SERVER_CRASH') return '#FF6B35';
    return '#FFC107';
  };

  // Update runtime every second when running
  useEffect(() => {
    // Update immediately
    setCurrentRuntime(calculateRuntime());
    
    if (isRunning && sessionInfo?.startTime) {
      // Set up interval to update every second
      timerRef.current = setInterval(() => {
        setCurrentRuntime(calculateRuntime());
      }, 1000);
    } else {
      // Clear interval if exists
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isRunning, sessionInfo, calculateRuntime]);

  // Also update when sessionInfo changes
  useEffect(() => {
    setCurrentRuntime(calculateRuntime());
  }, [sessionInfo, calculateRuntime]);

  const runtime = currentRuntime || calculateRuntime();
  const stopStatus = getStopStatus();
  const shutdownReason = getReasonDisplay(sessionInfo?.reason);
  const reasonColor = getReasonColor(sessionInfo?.reason);

  // If no session info, show default state
  if (!sessionInfo) {
    return (
      <div className="bot-duration-display idle">
        <div className="duration-header">
          <span className="duration-icon">🤖</span>
          <span className="duration-title">Bot Status</span>
        </div>
        <div className="duration-body">
          <div className="duration-row">
            <span className="duration-label">⏱️ Status:</span>
            <span className="duration-value">⏹️ Not Started</span>
          </div>
          <div className="duration-row">
            <span className="duration-label">📅 Started:</span>
            <span className="duration-value">N/A</span>
          </div>
          <div className="duration-row">
            <span className="duration-label">📅 Stopped:</span>
            <span className="duration-value">N/A</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bot-duration-display ${isRunning ? 'running' : sessionInfo.reason ? 'stopped' : 'idle'}`}>
      <div className="duration-header">
        <span className="duration-icon">🔄</span>
        <span className="duration-title">{isRunning ? 'Bot Running' : 'Bot Session Summary'}</span>
        {onClose && (
          <button className="duration-close" onClick={onClose}>
            <FaTimes />
          </button>
        )}
      </div>
      <div className="duration-body">
        <div className="duration-row">
          <span className="duration-label">⏱️ Total Runtime:</span>
          <span className="duration-value highlight">{runtime}</span>
        </div>
        <div className="duration-row">
          <span className="duration-label">📅 Started:</span>
          <span className="duration-value">{sessionInfo.startTime ? new Date(sessionInfo.startTime).toLocaleString() : 'N/A'}</span>
        </div>
        <div className="duration-row">
          <span className="duration-label">📅 Stopped:</span>
          <span className="duration-value">{stopStatus}</span>
        </div>
        {!isRunning && sessionInfo?.reason && (
          <div className="duration-row reason-row" style={{ borderColor: reasonColor }}>
            <span className="duration-label">📌 Shutdown Reason:</span>
            <span className="duration-value reason" style={{ color: reasonColor }}>
              {shutdownReason}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const TradingBot = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const tradingState = useSelector((state) => state.trading) || {
    tradingBot: { isRunning: false, config: { botToken: '', chatIds: [], enabled: false }, loading: false },
    positions: [],
    trades: [],
    accountInfo: null
  };
  
  const { tradingBot, positions, trades, accountInfo } = tradingState;
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [orderAmount, setOrderAmount] = useState(0.01);
  const [orderType, setOrderType] = useState('BUY');
  const [symbols, setSymbols] = useState([]);
  const [activeTab, setActiveTab] = useState('positions');
  const [orderLoading, setOrderLoading] = useState(false);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [symbolsLoaded, setSymbolsLoaded] = useState(false);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [channelCount, setChannelCount] = useState(0);
  const [isStarting, setIsStarting] = useState(false);
  const [showStartingModal, setShowStartingModal] = useState(false);
  const [modalComplete, setModalComplete] = useState(false);
  const [isCheckingBotStatus, setIsCheckingBotStatus] = useState(false);
  const [statusCheckDone, setStatusCheckDone] = useState(false);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [showStopSummary, setShowStopSummary] = useState(false);
  const [stopSummaryData, setStopSummaryData] = useState(null);
  const [hasCheckedStatus, setHasCheckedStatus] = useState(false);

  // Load symbols on mount
  useEffect(() => {
    if (user?.uid && !symbolsLoaded) {
      loadSymbols();
      setSymbolsLoaded(true);
    }
  }, [user, symbolsLoaded]);

  // Load bot config and setup listeners
  useEffect(() => {
    if (user?.uid) {
      dispatch(loadTradingBotConfig(user.uid));
      dispatch(loadPositions(user.uid));
      dispatch(loadTrades(user.uid));
      
      // Initial status check
      checkBotRunningStatus();
      setHasCheckedStatus(true);
      
      if (window.socket) {
        // Remove old listeners to avoid duplicates
        window.socket.off('position_opened');
        window.socket.off('position_closed');
        window.socket.off('account_update');
        window.socket.off('trading_error');
        window.socket.off('trading_bot_status');
        window.socket.off('connect');
        window.socket.off('disconnect');
        
        window.socket.on('position_opened', (position) => {
          dispatch(loadPositions(user.uid));
          dispatch(loadTrades(user.uid));
          toast.success(`New ${position.type} position opened on ${position.symbol}`);
        });
        
        window.socket.on('position_closed', (position) => {
          dispatch(loadPositions(user.uid));
          dispatch(loadTrades(user.uid));
          const profitEmoji = position.profit >= 0 ? '✅' : '❌';
          toast(`${profitEmoji} Position closed on ${position.symbol}`);
        });
        
        window.socket.on('account_update', (account) => {
          console.log('Account update received:', account);
        });
        
        window.socket.on('trading_error', (error) => {
          toast.error(`Trading error: ${error.error}`);
        });
        
        // Listen for bot status updates from backend
        window.socket.on('trading_bot_status', (data) => {
          if (data.userId === user?.uid) {
            console.log('📡 Bot status update received:', data);
            
            // Update running state
            dispatch(setTradingBotRunning(data.isRunning));
            
            // Update session info
            if (data.sessionInfo) {
              setSessionInfo(data.sessionInfo);
            } else if (data.stopInfo) {
              setSessionInfo(data.stopInfo);
            }
            
            // If stopped, show summary
            if (!data.isRunning && data.stopInfo) {
              setStopSummaryData(data.stopInfo);
              setShowStopSummary(true);
              setTimeout(() => {
                setShowStopSummary(false);
              }, 10000);
            }
            
            // If running, load positions and trades
            if (data.isRunning) {
              dispatch(loadPositions(user.uid));
              dispatch(loadTrades(user.uid));
              if (isStarting) {
                setModalComplete(true);
                setIsStarting(false);
                setTimeout(() => {
                  setShowStartingModal(false);
                  setModalComplete(false);
                }, 3000);
              }
            }
          }
        });

        // Handle socket reconnect - recheck status
        window.socket.on('connect', () => {
          console.log('🔄 Socket reconnected, checking bot status...');
          setStatusCheckDone(false);
          checkBotRunningStatus();
        });
      }
    }
    
    return () => {
      if (window.socket) {
        window.socket.off('position_opened');
        window.socket.off('position_closed');
        window.socket.off('account_update');
        window.socket.off('trading_error');
        window.socket.off('trading_bot_status');
        window.socket.off('connect');
        window.socket.off('disconnect');
      }
    };
  }, [user, dispatch]);

  // Monitor tradingBot.isRunning to close modal
  useEffect(() => {
    if (tradingBot?.isRunning && isStarting) {
      setModalComplete(true);
      setIsStarting(false);
      toast.success('Trading bot started successfully!');
      
      setTimeout(() => {
        setShowStartingModal(false);
        setModalComplete(false);
      }, 3000);
    }
  }, [tradingBot?.isRunning, isStarting]);

  // Update configured status when tradingBot.config changes
  useEffect(() => {
    if (tradingBot?.config) {
      const hasConfig = !!(tradingBot.config.botToken && 
        ((tradingBot.config.chatIds && tradingBot.config.chatIds.length > 0) || tradingBot.config.chatId));
      setIsConfigured(hasConfig);
      
      const count = tradingBot.config.chatIds?.length || (tradingBot.config.chatId ? 1 : 0);
      setChannelCount(count);
    }
  }, [tradingBot?.config]);

  const checkBotRunningStatus = async () => {
    if (!user?.uid || isCheckingBotStatus) return;
    
    setIsCheckingBotStatus(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/trading-bot/status/${user.uid}`);
      if (response.data) {
        const isRunning = response.data.isRunning || false;
        // Update Redux state to match backend
        if (tradingBot?.isRunning !== isRunning) {
          dispatch(setTradingBotRunning(isRunning));
          if (isRunning) {
            console.log('🔄 Bot is already running on backend - state synced');
            dispatch(loadPositions(user.uid));
            dispatch(loadTrades(user.uid));
          }
        }
        // Update session info
        if (response.data.sessionInfo) {
          setSessionInfo(response.data.sessionInfo);
        }
        setStatusCheckDone(true);
      }
    } catch (error) {
      console.error('Error checking bot status:', error);
      dispatch(setTradingBotRunning(false));
      setStatusCheckDone(true);
    } finally {
      setIsCheckingBotStatus(false);
    }
  };

  const loadSymbols = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/symbols`);
      setSymbols(response.data);
      if (response.data.length > 0) {
        setSelectedSymbol(response.data[0].value);
      }
    } catch (error) {
      console.error('Error loading symbols:', error);
      toast.error('Failed to load symbols');
    }
  };

  const handleStartBot = async () => {
    if (!isConfigured) {
      toast.error('Please configure Telegram bot token and chat ID first');
      setIsConfigModalOpen(true);
      return;
    }
    
    setIsStarting(true);
    setModalComplete(false);
    setShowStartingModal(true);
    
    try {
      const result = await dispatch(startTradingBot(user.uid));
      // The socket will handle the status update
      // But also check status after a delay to ensure we get the session info
      setTimeout(() => {
        checkBotRunningStatus();
      }, 2000);
    } catch (error) {
      console.error('Error starting bot:', error);
      setShowStartingModal(false);
      setIsStarting(false);
      setModalComplete(false);
      toast.error('Failed to start trading bot');
    }
  };

  const handleCloseModal = () => {
    setShowStartingModal(false);
    setIsStarting(false);
    setModalComplete(false);
  };

  const handleStopBot = async () => {
    try {
      const result = await dispatch(stopTradingBot(user.uid));
      // The socket will handle the status update
      setStatusCheckDone(false);
      // Also check status after a delay to ensure we get the session info
      setTimeout(() => {
        checkBotRunningStatus();
      }, 1000);
    } catch (error) {
      console.error('Error stopping bot:', error);
      toast.error('Failed to stop trading bot');
    }
  };

  const handlePlaceOrder = async () => {
    if (isPlacingOrder) {
      toast.error('Order already in progress. Please wait.');
      return;
    }
    
    if (!selectedSymbol) {
      toast.error('Please select a symbol');
      return;
    }
    if (!orderAmount || orderAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!tradingBot?.isRunning) {
      toast.error('Trading bot is not running. Please start the bot first.');
      return;
    }
    
    setIsPlacingOrder(true);
    setOrderLoading(true);
    
    try {
      const success = await dispatch(placeOrder(user.uid, selectedSymbol, orderAmount, orderType));
      if (success) {
        toast.success(`${orderType} order placed for ${selectedSymbol}`);
        setOrderAmount(0.01);
      }
    } catch (error) {
      console.error('Order error:', error);
      toast.error('Failed to place order');
    } finally {
      setIsPlacingOrder(false);
      setOrderLoading(false);
    }
  };

  const handleConfigSaved = (config) => {
    setIsConfigured(true);
    setChannelCount(config.chatIds?.length || 1);
    setStatusCheckDone(false);
    setTimeout(() => {
      checkBotRunningStatus();
    }, 500);
  };

  const tabs = [
    { id: 'positions', label: '📊 Positions' },
    { id: 'history', label: '📜 Trade History' },
    { id: 'news', label: '📰 Economic News' },
    { id: 'logs', label: '📋 Logs' }
  ];

  return (
    <>
      <StartingModal 
        isOpen={showStartingModal}
        isStarting={isStarting}
        isComplete={modalComplete}
        onClose={handleCloseModal}
      />

      {showStopSummary && stopSummaryData && (
        <div className="stop-summary-overlay" onClick={() => setShowStopSummary(false)}>
          <div className="stop-summary-modal" onClick={(e) => e.stopPropagation()}>
            <BotDurationDisplay 
              sessionInfo={stopSummaryData} 
              isRunning={false}
              onClose={() => setShowStopSummary(false)}
            />
          </div>
        </div>
      )}

      <TelegramConfigModal 
        isOpen={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
        onSaved={handleConfigSaved}
      />
      
      <div className="trading-bot-container">
        <div className="card trading-controls">
          <div className="card-header">
            <h2>🤖 Trading Bot (MetaApi)</h2>
            <div className="bot-status">
              <span className={`status-indicator ${tradingBot?.isRunning ? 'running' : 'stopped'}`}>
                {isCheckingBotStatus ? '🔄 Checking...' : tradingBot?.isRunning ? '🟢 Running' : '🔴 Stopped'}
              </span>
              {!tradingBot?.isRunning ? (
                <button onClick={handleStartBot} className="btn btn-success" disabled={isStarting || isCheckingBotStatus}>
                  {isStarting ? <FaSpinner className="spinning" /> : <FaPlay />} 
                  {isStarting ? 'Starting...' : 'Start Bot'}
                </button>
              ) : (
                <button onClick={handleStopBot} className="btn btn-danger">
                  <FaStop /> Stop Bot
                </button>
              )}
              <button onClick={() => setIsConfigModalOpen(true)} className="btn btn-secondary">
                <FaCog /> {isConfigured ? `${channelCount} Channel(s)` : 'Configure'}
              </button>
            </div>
          </div>

          {/* Bot Duration Display - ALWAYS VISIBLE */}
          <BotDurationDisplay 
            sessionInfo={sessionInfo} 
            isRunning={tradingBot?.isRunning}
          />
          
          {accountInfo && (
            <div className="account-summary">
              <div className="account-card">
                <span className="label">Balance</span>
                <span className="value">${accountInfo.balance?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="account-card">
                <span className="label">Equity</span>
                <span className="value">${accountInfo.equity?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="account-card">
                <span className="label">Free Margin</span>
                <span className="value">${accountInfo.marginFree?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="account-card">
                <span className="label">Open Positions</span>
                <span className="value">{positions?.length || 0}</span>
              </div>
            </div>
          )}
          
          {tradingBot?.isRunning && (
            <div className="quick-order-panel">
              <h3>Quick Trade (Manual)</h3>
              <div className="order-controls">
                <select 
                  value={selectedSymbol} 
                  onChange={(e) => setSelectedSymbol(e.target.value)}
                  className="order-symbol"
                >
                  {symbols.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                <input 
                  type="number" 
                  value={orderAmount} 
                  onChange={(e) => setOrderAmount(parseFloat(e.target.value))}
                  placeholder="Volume (lots)"
                  className="order-amount"
                  step="0.01"
                  min="0.01"
                />
                <div className="order-type-buttons">
                  <button 
                    className={`order-btn buy ${orderType === 'BUY' ? 'active' : ''}`}
                    onClick={() => setOrderType('BUY')}
                  >
                    <FaArrowUp /> BUY
                  </button>
                  <button 
                    className={`order-btn sell ${orderType === 'SELL' ? 'active' : ''}`}
                    onClick={() => setOrderType('SELL')}
                  >
                    <FaArrowDown /> SELL
                  </button>
                </div>
                <button 
                  onClick={handlePlaceOrder} 
                  className="btn btn-primary"
                  disabled={orderLoading || !tradingBot?.isRunning || isPlacingOrder}
                >
                  {orderLoading ? <FaSpinner className="spinning" /> : 'Place Order'}
                </button>
              </div>
              <small className="order-note">💡 This places a market order on your MetaTrader account via MetaApi.</small>
            </div>
          )}
        </div>
        
        <div className="trading-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        
        <div className="tab-content">
          {activeTab === 'positions' && <PositionTracker />}
          {activeTab === 'history' && <TradeHistory />}
          {activeTab === 'news' && <NewsCalendar />}
          {activeTab === 'logs' && <LogViewer />}
        </div>
      </div>
    </>
  );
};

export default TradingBot;